import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";

import { API_URL } from "../config/apiConfig";
import { ApiResponse } from "../types/common";
import { useAuthStore } from "../store/useAuthStore";

const DEFAULT_TIMEOUT = 10000; // 10s
const AUTH_TIMEOUT = 30000; // 30s for slow auth/login
const PAYMENT_TIMEOUT = 60000; // 60s for payment/stripe flows
const LONG_TIMEOUT = Math.max(AUTH_TIMEOUT, PAYMENT_TIMEOUT);
const MAX_RETRIES = 2; // retry 2x for transient issues

// Fix #5: Navigation callback — set by App root so interceptor can use React Router
// instead of window.location.href (which causes a full page reload)
let _navigate: ((path: string) => void) | null = null;
export const setNavigate = (fn: (path: string) => void): void => {
  _navigate = fn;
};
const navigateTo = (path: string): void => {
  if (_navigate) {
    _navigate(path);
  } else {
    window.location.href = path; // fallback before Router mounts
  }
};

const isGoogleAuthEndpoint = (url?: string) =>
  !!url && /\/google-authenticate|\/auth\/google/i.test(url);
const isAuthEndpoint = (url?: string) => !!url && /\/auth(\/|$)/i.test(url);
const isPaymentEndpoint = (url?: string) =>
  !!url && (/\/payment(\/|$)/i.test(url) || /\/stripe(\/|$)/i.test(url));

// Single axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
  timeout: DEFAULT_TIMEOUT,
});

// --- Refresh token state ---
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // Give auth/payment endpoints more time on slow networks
    if (isAuthEndpoint(config.url)) {
      config.timeout = Math.max(config.timeout ?? 0, AUTH_TIMEOUT);
    }
    if (isPaymentEndpoint(config.url)) {
      config.timeout = Math.max(config.timeout ?? 0, PAYMENT_TIMEOUT);
    }

    // Give Google auth endpoints more time
    if (isGoogleAuthEndpoint(config.url)) {
      config.timeout = Math.max(config.timeout ?? 0, LONG_TIMEOUT);
    }

    // init retry meta
    (config as any).__retryCount ??= 0;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor (errors, retries)
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const cfg = (error.config || {}) as AxiosRequestConfig & {
      __retryCount?: number;
    };
    const url = cfg.url || "";

    // ----- RETRY: timeouts & 5xx on google auth endpoints only -----
    const isTimeout =
      error.code === "ECONNABORTED" && /timeout/i.test(error.message);
    const is5xx = !!error.response && error.response.status >= 500;
    const canRetry = isGoogleAuthEndpoint(url) && (isTimeout || is5xx);

    if (canRetry && (cfg.__retryCount ?? 0) < MAX_RETRIES) {
      cfg.__retryCount = (cfg.__retryCount ?? 0) + 1;
      const backoffMs = 500 * Math.pow(2, cfg.__retryCount - 1); // 500, 1000

      await new Promise((r) => setTimeout(r, backoffMs));
      return apiClient(cfg); // retry
    }

    // ----- 401: Silent token refresh -----

    if (error.response?.status === 401) {
      const isLoginRequest = url.includes("/login");
      if (isLoginRequest) {
        return Promise.reject(error);
      }

      const isPayment = url.includes("/payment") || url.includes("/stripe");
      const originalRequest = error.config;

      // Don't retry refresh requests, payment 401s, or already-retried requests
      if (
        url.includes("/auth/refresh") ||
        isPayment ||
        (originalRequest as any)?.__isRetry
      ) {
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        navigateTo("/login");
        return Promise.reject(error);
      }

      const storedRefreshToken = localStorage.getItem("refreshToken");

      if (!storedRefreshToken) {
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        navigateTo("/login");
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (originalRequest) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            (originalRequest as any).__isRetry = true;
            return apiClient(originalRequest);
          }
          return Promise.reject(error);
        });
      }

      isRefreshing = true;

      return apiClient
        .post("/auth/refresh", { refreshToken: storedRefreshToken })
        .then((response) => {
          const data = response.data?.data;
          const newAccessToken = data?.accessToken;
          const newRefreshToken = data?.refreshToken;

          if (newAccessToken) {
            localStorage.setItem("token", newAccessToken);
            sessionStorage.setItem("token", newAccessToken);
          }
          if (newRefreshToken) {
            localStorage.setItem("refreshToken", newRefreshToken);
          }

          processQueue(null, newAccessToken);

          // Retry original request
          if (originalRequest) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            (originalRequest as any).__isRetry = true;
            return apiClient(originalRequest);
          }
          return Promise.reject(error);
        })
        .catch((refreshError) => {
          processQueue(refreshError, null);
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          navigateTo("/login");
          return Promise.reject(refreshError);
        })
        .finally(() => {
          isRefreshing = false;
        });
    } else if (error.code === "ERR_NETWORK") {
      // Network error - no action
    } else if (error.response?.status === 403) {
      // Only treat 403 as auth failure for actual auth/user endpoints.
      // A 403 from products, search, or external API proxies (Zinc) is NOT
      // an auth failure — it means the external service denied our request.
      // Clearing tokens and redirecting to /login on those 403s caused a
      // redirect loop: Zinc 403 → clear tokens → /login → login → Zinc 403...
      const isAuthRelated =
        url.includes("/auth/") ||
        url.includes("/users/me") ||
        url.includes("/account") ||
        url.includes("/admin/");

      if (isAuthRelated) {
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("refreshToken");
        navigateTo("/login");
      }
      // For non-auth 403s (e.g. Zinc API, products, search) — just reject,
      // let the component handle the error gracefully.
    } else if (error.response?.status === 404) {
      // 404 - no action
    }

    return Promise.reject(error);
  }
);

// Helper to force long-timeout per call when needed
export const withLongTimeout = (
  config?: AxiosRequestConfig
): AxiosRequestConfig => ({
  ...(config || {}),
  timeout: Math.max(config?.timeout ?? 0, LONG_TIMEOUT),
});

// --- ApiClient wrapper unchanged except we expose config on create/custom ---

class ApiClient<T> {
  endpoint: string;
  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  getAll = (config?: AxiosRequestConfig) =>
    apiClient.get<ApiResponse<T[]>>(this.endpoint, config).then((r) => r.data);

  get = (id: string | number, config?: AxiosRequestConfig) =>
    apiClient
      .get<ApiResponse<T>>(`${this.endpoint}/${id}`, config)
      .then((r) => r.data);

  create = (data: Partial<T>, config?: AxiosRequestConfig) =>
    apiClient
      .post<ApiResponse<T>>(this.endpoint, data, config)
      .then((r) => r.data);

  update = (
    id: string | number,
    data: Partial<T>,
    config?: AxiosRequestConfig
  ) =>
    apiClient
      .put<ApiResponse<T>>(`${this.endpoint}/${id}`, data, config)
      .then((r) => r.data);

  delete = (id: string | number, config?: AxiosRequestConfig) =>
    apiClient
      .delete<ApiResponse<void>>(`${this.endpoint}/${id}`, config)
      .then((r) => r.data);

  custom = (
    method: "get" | "post" | "put" | "delete",
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ) =>
    apiClient
      .request({ method, url: `${this.endpoint}${url}`, data, ...config })
      .then((r) => r.data);
}

export default apiClient;
export { ApiClient };
